'use client';
import React from 'react';
import { WeeklyInsight } from '@/lib/services/forgeBehaviorService';

interface WeeklyInsightReportProps {
  insight: WeeklyInsight;
}

export default function WeeklyInsightReport({ insight }: WeeklyInsightReportProps) {
  return (
    <div className="card-forge p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-forge-heading font-bold tracking-wider text-sm uppercase">What The Data Says</h3>
          <p className="text-2xs text-muted-foreground mt-0.5">
            {insight.trackedDays} days tracked · Minimum 10 required for conclusions
          </p>
        </div>
        <div className={`px-2 py-1 rounded text-2xs font-bold tracking-wider ${
          insight.hasEnoughData
            ? 'bg-green-500/15 text-green-400 border border-green-500/20' :'bg-muted/20 text-muted-foreground border border-border'
        }`}>
          {insight.hasEnoughData ? 'ACTIVE' : 'BUILDING'}
        </div>
      </div>

      {!insight.hasEnoughData ? (
        <div className="py-4 space-y-3">
          <div className="bg-card-elevated rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Data collection progress</span>
              <span className="text-xs font-bold text-foreground">{insight.trackedDays}/10 days</span>
            </div>
            <div className="h-2 bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${Math.min((insight.trackedDays / 10) * 100, 100)}%` }}
              />
            </div>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            Keep logging daily behaviors. Insights will appear once you have 10+ tracked days.
          </p>
          <p className="text-xs text-muted-foreground/60 text-center italic">
            The forge reveals the truth. Measure first. Decide second.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {insight.insights.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No significant patterns detected yet. Keep logging.</p>
          ) : (
            insight.insights.map((text, i) => (
              <div key={i} className="flex gap-3 items-start p-3 bg-card-elevated rounded-lg">
                <span className="text-primary mt-0.5 shrink-0">◆</span>
                <p className="text-sm text-foreground">{text}</p>
              </div>
            ))
          )}

          <div className="pt-2 border-t border-border space-y-1.5">
            {insight.cannabisNeutralToWeightLoss && (
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full bg-muted-foreground/40 shrink-0" />
                <span className="text-muted-foreground">Cannabis appears neutral to your weight-loss progress.</span>
              </div>
            )}
            {insight.cannabisAssociatedWithExtraCalories && (
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                <span className="text-muted-foreground">Cannabis appears associated with increased unplanned calories.</span>
              </div>
            )}
            {insight.cannabisAssociatedWithBetterSleep && (
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                <span className="text-muted-foreground">Cannabis appears associated with improved sleep consistency.</span>
              </div>
            )}
            {insight.cannabisAssociatedWithLowerProtein && (
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full bg-red-400 shrink-0" />
                <span className="text-muted-foreground">Cannabis appears associated with lower daily protein intake.</span>
              </div>
            )}
          </div>

          <p className="text-2xs text-muted-foreground/50 italic pt-1">
            Correlation does not necessarily equal causation. These are observations only.
            FORGE does not judge behavior. FORGE reveals patterns. The user decides what to do with the truth.
          </p>
        </div>
      )}
    </div>
  );
}
